<template>
  <div id="vue-app">
    <Head></Head>
    <navigation-bar></navigation-bar>
    <div class="main-content">
      <RouterView></RouterView>
    </div>
    <footer-bar v-if="shouldShowFooterBar"></footer-bar>
  </div>
</template>
<script lang="ts">
import VueScrollTo from 'vue-scrollto';
import NavigationBar from './app/components/navigation-bar/navigation-bar.vue';
import FooterBar from './app/components/footer-bar/footer-bar.vue';
import { areWeInSearchPage, error } from './app/routes';
import * as actions from '@/app/store/action-types';
import config from '@/config';
import { throttle, first } from 'lodash-es';
import isSSR from './utils/isSSR';
import { defineComponent, onBeforeMount } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import Head from './app/components/Head.vue';
import useSafeRoute from './app/composables/useSafeRoute';
import { opLandingPagesQueryKey } from './app/data/useLandingPagesDataNew';
import { landingPagesQuery_Client } from './app/data/landingPages.client';
import logger from '@/app/logger';

export default defineComponent({
  setup() {
    const queryClient = useQueryClient();
    const route = useSafeRoute();

    // Function to preload images
    const preloadImage = (url: string) => {
      const img = new Image();
      img.src = url;
    };

    onBeforeMount(() => {
      const prefetchLandingPages = async () => {
        if (route.value.path !== error.path) {
          logger.info('Prefetch all landing pages when the page is loaded');
          const result = await queryClient.fetchQuery({
            queryKey: [opLandingPagesQueryKey],
            queryFn: () => landingPagesQuery_Client(),
          });
          if (result.data) {
            result.data.forEach((page) => {
              queryClient.setQueryData([opLandingPagesQueryKey, page.pageName], page);
              // Preload hero images
              if (page.heroImage) {
                preloadImage(page.heroImage + '?fm=webp');
              }
            });
          }
        }
      };

      if (document.readyState === 'complete') {
        void prefetchLandingPages();
      } else {
        window.addEventListener('load', () => {
          void prefetchLandingPages();
        });
      }
    });

    return { route };
  },
  computed: {
    metaInfo() {
      return {
        title: 'OP Koti',
        meta: [
          { 'http-equiv': 'Content-type', content: 'text/html; charset=utf-8' },
          {
            name: 'viewport',
            content: 'width=device-width, initial-scale=1.0, maximum-scale=5.0',
          },
          {
            vmid: 'description',
            name: 'description',
            content:
              'OP Koti kokoaa myytävät ja vuokrattavat asunnot eri puolilta Suomea saman katon alle. Tutustu palveluun ja löydä uusi kotisi helposti!',
          },
        ],
        link: [
          {
            rel: 'shortcut icon',
            href: 'https://op-koti.fi/assets/favicon.ico',
            type: 'image/x-icon',
          },
        ],
      };
    },
    shouldShowFooterBar() {
      return !areWeInSearchPage(this.route.path);
    },
  },
  methods: {
    emitWindowResizedEvents() {
      this.$bus.$emit('windowResized');
      this.$bus.$emit(`windowResized-${this.route.path}`);
    },
    waitForElementAndScroll(el: HTMLElement | null) {
      if (!el) {
        return;
      }
      const that = this;
      setTimeout(function () {
        if (el.offsetTop > 0) {
          VueScrollTo.scrollTo(el);
        } else {
          that.waitForElementAndScroll(el);
        }
      }, 100);
    },
  },
  created() {
    if (isSSR) {
      return;
    }

    if (window.localStorage.getItem('opKotiOptOut')) {
      // @ts-ignore
      if (parseInt(window.localStorage.getItem('opKotiOptOut'), 10) === config.GDPR_CONSENT_OPTIONS.DISALLOW_ALL) {
        void this.$store.dispatch(actions.analytics.suspend, true);
      }
    }
  },
  mounted() {
    void this.$store.dispatch(actions.analytics.ocmBannerReady, true);

    const touch = 'ontouchstart' in document.documentElement;

    if (!touch) {
      document.documentElement.className += ' no-touch';
    }

    this.$el.addEventListener('click', (event: MouseEvent) => {
      const expanders = document.querySelectorAll('.expander-box, .filter-box, .toggle-sub-navigation');
      let willFireExpanderClose = true;

      Array.prototype.forEach.call(expanders, (expander) => {
        if (expander.contains(event.target)) {
          willFireExpanderClose = false;
        }
      });

      if (willFireExpanderClose) {
        this.$bus.$emit('collapseExpanders');
      }
    });

    window.addEventListener(
      'resize',
      throttle(() => {
        this.emitWindowResizedEvents();
      }, 200)
    );

    window.addEventListener(
      'orientationchange',
      throttle(
        () => {
          this.emitWindowResizedEvents();
        },
        200,
        { leading: false }
      )
    ); // Needed at least for Chrome on iPad

    /*
        NOTE: This is a hack to enable scrolling directly to
        anchor element when coming to OP Koti from another site (external links)
        TODO: Requires explanation what type of scenarios this should support
      */
    if (!isSSR) {
      const $scrollSectionEl = first(
        config.SCROLL_SECTIONS.filter((scrollSection) => Object.keys(this.route.query).includes(scrollSection))
      );

      const section = this.route.query?.section;
      if (section && !Array.isArray(section) && $scrollSectionEl) {
        if (config.SCROLL_SECTIONS.includes(section) || !!$scrollSectionEl) {
          const el = document.getElementById(section) || document.getElementById($scrollSectionEl);
          this.waitForElementAndScroll(el);
        }
      }
    }
  },
  components: {
    NavigationBar,
    FooterBar,
    Head,
  },
});
</script>
